/**
 * localStorage helpers for The Differential.
 *
 * Each day's game is stored under key: thediff_YYYY-MM-DD
 * Leaderboard (sorted history) stored under: thediff_leaderboard
 * Archive (completed cases) stored under: thediff_archive
 */

const STORAGE_PREFIX = 'thediff_';
const LB_KEY = 'thediff_leaderboard';
const ARCHIVE_KEY = 'thediff_archive';

function getTodayKey() {
  return STORAGE_PREFIX + new Date().toISOString().split('T')[0];
}

export function loadGameState() {
  try {
    const raw = localStorage.getItem(getTodayKey());
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveGameState(state) {
  try {
    localStorage.setItem(getTodayKey(), JSON.stringify(state));
  } catch { /* storage full */ }
}

export function initGameState(dayNumber) {
  return {
    day: dayNumber,
    status: 'waiting',
    moves: 0,
    start_time: null,
    end_time: null,
    score: null,
    ordered: [],
    feed: [],
    guesses: 0,
    used_snark_indices: {},
  };
}

export function getElapsedSeconds(startTime) {
  if (!startTime) return 0;
  return Math.floor((Date.now() - startTime) / 1000);
}

/** Leaderboard */
export function saveToLeaderboard(entry) {
  try {
    const existing = JSON.parse(localStorage.getItem(LB_KEY) || '[]');
    // Don't duplicate the same day
    const filtered = existing.filter(e => e.day !== entry.day);
    filtered.unshift(entry);
    localStorage.setItem(LB_KEY, JSON.stringify(filtered.slice(0, 60)));
  } catch { /* ignore */ }
}

export function getLeaderboard() {
  try {
    return JSON.parse(localStorage.getItem(LB_KEY) || '[]');
  } catch {
    return [];
  }
}

/**
 * Archive — a separate, explicit list of completed cases.
 * Stored as an array of summaries, newest first.
 */
export function pushToArchive(entry) {
  try {
    const existing = JSON.parse(localStorage.getItem(ARCHIVE_KEY) || '[]');
    // Don't duplicate the same day
    const filtered = existing.filter(e => e.day !== entry.day);
    filtered.unshift(entry);
    localStorage.setItem(ARCHIVE_KEY, JSON.stringify(filtered.slice(0, 120)));
  } catch { /* ignore */ }
}

export function getArchive() {
  try {
    return JSON.parse(localStorage.getItem(ARCHIVE_KEY) || '[]');
  } catch {
    return [];
  }
}

export function getGradeLabel(score) {
  if (score === null || score === undefined) return '?';
  if (score >= 90) return 'S';
  if (score >= 75) return 'A';
  if (score >= 55) return 'B';
  if (score >= 30) return 'C';
  if (score >= 1)  return 'D';
  return 'F';
}

// ─────────────────────────────────────────────────────────────────────────────
// STREAK — tracks consecutive days played
// Based on Day Number (case number), not calendar date.
// This avoids timezone bugs: if you play at 11:58 PM, you still get credit.
// Archive replays do NOT count — only today's live game updates the streak.
//
// Legacy wrap: a server bug stored days as (n % 100) + 1 after Day 100, so
// Day 101 was saved as 1 and Day 102 as 2. Helpers below remap those values
// so streaks and "already played today" checks keep working.
// ─────────────────────────────────────────────────────────────────────────────
const STREAK_KEY = 'thediff_streak';
const LEGACY_CYCLE = 100; // must match server/lib/dayNumber.js
const WRAP_GRACE_DAYS = 7; // forgive sitting out the reset days

export function legacyWrappedDay(dayNumber) {
  if (dayNumber < 1) return 1;
  return ((dayNumber - 1) % LEGACY_CYCLE) + 1;
}

function writeStreak(updated) {
  localStorage.setItem(STREAK_KEY, JSON.stringify(updated));
  return updated;
}

export function getStreakData() {
  try {
    const raw = localStorage.getItem(STREAK_KEY);
    return raw
      ? JSON.parse(raw)
      : { current: 0, best: 0, lastDay: null };
  } catch {
    return { current: 0, best: 0, lastDay: null };
  }
}

/**
 * One-time remap of lastDay from the 100-day wrap back onto sequential days.
 * Does not change current/best — only the stored day pointer.
 * Safe to call on every load.
 */
export function reconcileStreak(todayDay) {
  try {
    const data = getStreakData();
    if (!data.lastDay || !todayDay || todayDay <= LEGACY_CYCLE) return data;
    if (data.lastDay === todayDay || data.lastDay === todayDay - 1) return data;
    // Only rewrite wrap-era values (Day 101 stored as 1, etc.) during the
    // short window after the cycle, so an old Day 51 save cannot look like
    // "yesterday" 100 days later.
    if (todayDay > LEGACY_CYCLE + WRAP_GRACE_DAYS) return data;

    let lastDay = data.lastDay;
    const daysIntoWrap = todayDay - LEGACY_CYCLE;
    const wrappedToday = legacyWrappedDay(todayDay);
    const wrappedYesterday = legacyWrappedDay(todayDay - 1);

    if (lastDay === wrappedToday && lastDay <= daysIntoWrap) {
      lastDay = todayDay;
    } else if (lastDay === wrappedYesterday && lastDay <= daysIntoWrap) {
      lastDay = todayDay - 1;
    } else if (lastDay === LEGACY_CYCLE) {
      // Played Day 100, then sat out the bogus Day 1/2 reset
      lastDay = todayDay - 1;
    } else {
      return data;
    }

    return writeStreak({
      current: data.current || 0,
      best: data.best || 0,
      lastDay,
    });
  } catch {
    return getStreakData();
  }
}

/**
 * Call this when a live game completes (win or loss).
 * Pass the case's day number.
 * Do NOT call this for archive replays.
 */
export function updateStreak(dayNumber) {
  try {
    reconcileStreak(dayNumber);
    const data = getStreakData();
    const today = dayNumber;

    if (data.lastDay === today) return data; // Already counted today — idempotent

    let newCurrent;
    if (data.lastDay === today - 1) {
      newCurrent = (data.current || 0) + 1;
    } else {
      newCurrent = 1;
    }

    return writeStreak({
      current: newCurrent,
      best: Math.max(data.best || 0, newCurrent),
      lastDay: today,
    });
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// STATS SUBMISSION GUARD
// Ensures each player submits stats for a given day exactly once, ever.
// Once marked, no further write calls will be made for that day number.
// Archive replays must NEVER submit — always check isArchiveReplay first.
// ─────────────────────────────────────────────────────────────────────────────
const STATS_SUBMITTED_KEY = 'thediff_stats_submitted';

/**
 * Returns true if this browser has already submitted stats for dayNumber.
 */
export function hasSubmittedStats(dayNumber) {
  try {
    const raw = localStorage.getItem(STATS_SUBMITTED_KEY);
    const submitted = raw ? JSON.parse(raw) : {};
    return submitted[String(dayNumber)] === true;
  } catch {
    return false;
  }
}

/**
 * Marks dayNumber as submitted. Call this immediately after submitStats fires.
 * Keeps only the last 10 days to avoid unbounded localStorage growth.
 */
export function markStatsSubmitted(dayNumber) {
  try {
    const raw = localStorage.getItem(STATS_SUBMITTED_KEY);
    const submitted = raw ? JSON.parse(raw) : {};
    submitted[String(dayNumber)] = true;
    // Prune to last 10 day entries only
    const keys = Object.keys(submitted).map(Number).sort((a, b) => b - a).slice(0, 10);
    const pruned = {};
    keys.forEach(k => { pruned[String(k)] = true; });
    localStorage.setItem(STATS_SUBMITTED_KEY, JSON.stringify(pruned));
  } catch { /* ignore */ }
}

/**
 * Clears the submitted flag for a specific day, allowing a retry.
 * Use this to unblock players who were marked during a server outage.
 */
export function clearStatsSubmitted(dayNumber) {
  try {
    const raw = localStorage.getItem(STATS_SUBMITTED_KEY);
    const submitted = raw ? JSON.parse(raw) : {};
    delete submitted[String(dayNumber)];
    localStorage.setItem(STATS_SUBMITTED_KEY, JSON.stringify(submitted));
  } catch { /* ignore */ }
}
